import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Plus, Copy, Archive, Star, Edit3, Sparkles, X, Loader, Download } from 'lucide-react'
import api from '../../services/api'

const ROLE_BADGES: Record<string, string> = {
  'All Workers': 'var(--accent)',
  'Field Technician': 'var(--hseq-safety)',
  'Office Staff': 'var(--hseq-environment)',
  'Contractor': 'var(--orange)',
  'Supervisor': 'var(--purple)',
}

function AiGeneratorModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const [description, setDescription] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [preview, setPreview] = useState<any>(null)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const generate = async () => {
    if (!description.trim()) return
    setGenerating(true)
    setError('')
    setPreview(null)
    try {
      const { data } = await api.post('/onboarding/generate', { description })
      setPreview(data.template)
      if (!templateName) setTemplateName(data.template.name)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  const save = async () => {
    if (!preview) return
    setSaving(true)
    try {
      const { data } = await api.post('/onboarding/generate', {
        description,
        save: true,
        name: templateName || preview.name,
        role_type: preview.role_type,
      })
      onSaved(data.id)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const timingLabel: Record<string, string> = {
    before_day1: 'Before Day 1', day1: 'Day 1', week1: 'Week 1', month1: 'Month 1', ongoing: 'Ongoing',
  }
  const catEmoji: Record<string, string> = {
    legal: '🏛', documentation: '📋', safety: '🛡', role_training: '🔧', system_access: '📱', meet_team: '🤝',
  }

  const grouped = preview ? (preview.items || []).reduce((acc: any, item: any) => {
    const t = item.due_timing || 'day1'
    if (!acc[t]) acc[t] = []
    acc[t].push(item)
    return acc
  }, {}) : {}

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-12 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 680, padding: 28, display: 'flex', flexDirection: 'column', gap: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkles size={20} style={{ color: 'var(--purple)' }} />
            <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--text)' }}>Generate Template with AI</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
        </div>

        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Describe the role</p>
          <textarea
            rows={4}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g. Field electrician working on residential meter installations in NSW. Will do live electrical work and work at heights. Holds A-grade electrical licence."
            style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontSize: 14 }}
          />
          <button
            onClick={generate}
            disabled={!description.trim() || generating}
            className="btn btn-primary"
            style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {generating ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</> : <><Sparkles size={15} /> Generate Checklist</>}
          </button>
        </div>

        {error && <div style={{ padding: '10px 14px', background: 'var(--red-light)', borderRadius: 8, color: 'var(--red)', fontSize: 13 }}>{error}</div>}

        {preview && (
          <>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  style={{ flex: 1, fontWeight: 700, fontSize: 16, border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px' }}
                  placeholder="Template name"
                />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{preview.role_type}</span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>{preview.description}</p>

              <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {['before_day1', 'day1', 'week1', 'month1', 'ongoing'].map(timing => {
                  const grpItems = grouped[timing]
                  if (!grpItems?.length) return null
                  return (
                    <div key={timing}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {timingLabel[timing]} ({grpItems.length} items)
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {grpItems.map((item: any, i: number) => (
                          <div key={i} style={{
                            padding: '8px 12px', borderRadius: 8, background: 'var(--bg)',
                            border: `1px solid ${item.blocking ? 'var(--red)' : 'var(--border)'}`,
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                          }}>
                            <span style={{ fontSize: 14, flexShrink: 0 }}>{catEmoji[item.category] || '📋'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{item.name}</p>
                              {item.description && <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>{item.description}</p>}
                              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                                {item.blocking && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 600 }}>BLOCKING</span>}
                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{item.responsible}</span>
                                {item.evidence_type !== 'none' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--accent-light)', color: 'var(--accent)' }}>evidence: {item.evidence_type}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setPreview(null); setTemplateName('') }}>Regenerate</button>
              <button className="btn btn-primary" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Template'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function OnboardingTemplates() {
  const [templates, setTemplates] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAI, setShowAI] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [newForm, setNewForm] = useState({ name: '', role_type: '', description: '' })
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [seedResult, setSeedResult] = useState<{ created: string[]; skipped: string[] } | null>(null)

  const load = () => {
    setLoading(true)
    api.get('/onboarding/templates').then(r => setTemplates(r.data.data)).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const seedDefaults = async () => {
    setSeeding(true)
    try {
      const { data } = await api.post('/onboarding/seed-defaults')
      setSeedResult(data)
      load()
    } finally {
      setSeeding(false)
    }
  }

  const createTemplate = async () => {
    if (!newForm.name) return
    setSaving(true)
    try {
      await api.post('/onboarding/templates', newForm)
      setShowNew(false)
      setNewForm({ name: '', role_type: '', description: '' })
      load()
    } finally {
      setSaving(false)
    }
  }

  const setDefault = async (id: string) => {
    await api.put(`/onboarding/templates/${id}`, { is_default: true })
    load()
  }

  const clone = async (id: string) => {
    await api.post(`/onboarding/templates/${id}/clone`)
    load()
  }

  const archive = async (id: string) => {
    if (!confirm('Archive this template? It will no longer appear in the list.')) return
    await api.put(`/onboarding/templates/${id}`, { is_archived: true })
    load()
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Onboarding Templates</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Define what every new worker must complete before they can be assigned work.</p>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
          <button onClick={seedDefaults} disabled={seeding} className="btn btn-secondary flex items-center gap-2">
            {seeding ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={15} />}
            {seeding ? 'Loading…' : 'Load Standard Templates'}
          </button>
          <button onClick={() => setShowAI(true)} className="btn btn-secondary flex items-center gap-2">
            <Sparkles size={15} /> Generate with AI
          </button>
          <button onClick={() => setShowNew(true)} className="btn btn-primary flex items-center gap-2">
            <Plus size={15} /> New Template
          </button>
        </div>
      </div>

      {seedResult && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--green-light)', border: '1px solid var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600, margin: 0 }}>
            {seedResult.created.length > 0 ? `Created: ${seedResult.created.join(', ')}` : 'All standard templates already exist'}
            {seedResult.skipped.length > 0 && seedResult.created.length > 0 ? ` · Skipped: ${seedResult.skipped.join(', ')}` : ''}
          </p>
          <button onClick={() => setSeedResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)' }}><X size={14} /></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : templates.length === 0 ? (
        <div className="card p-12 text-center">
          <ClipboardList size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold text-lg mb-2" style={{ color: 'var(--text)' }}>No onboarding templates yet</p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Load the 5 standard AU WHS templates, generate one with AI, or build your own.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button onClick={seedDefaults} disabled={seeding} className="btn btn-primary flex items-center gap-2"><Download size={15} /> {seeding ? 'Loading…' : 'Load Standard Templates'}</button>
            <button onClick={() => setShowAI(true)} className="btn btn-secondary flex items-center gap-2"><Sparkles size={15} /> Generate with AI</button>
            <button onClick={() => setShowNew(true)} className="btn btn-secondary flex items-center gap-2"><Plus size={15} /> Create Manually</button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(tmpl => (
            <div key={tmpl.id} className="card p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold" style={{ color: 'var(--text)' }}>{tmpl.name}</p>
                    {tmpl.is_default === 1 && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-light)', color: 'var(--accent)', fontWeight: 600 }}>DEFAULT</span>
                    )}
                    {tmpl.role_type && (
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{tmpl.role_type}</span>
                    )}
                  </div>
                  {tmpl.description && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{tmpl.description}</p>}
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {tmpl.item_count || 0} items · Created {new Date(tmpl.created_at).toLocaleDateString('en-AU')}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {tmpl.is_default !== 1 && (
                    <button onClick={() => setDefault(tmpl.id)} title="Set as default"
                      className="btn btn-secondary p-2" style={{ minHeight: 'auto', padding: '6px' }}>
                      <Star size={14} />
                    </button>
                  )}
                  <button onClick={() => clone(tmpl.id)} title="Clone"
                    className="btn btn-secondary p-2" style={{ minHeight: 'auto', padding: '6px' }}>
                    <Copy size={14} />
                  </button>
                  <button onClick={() => archive(tmpl.id)} title="Archive"
                    className="btn btn-secondary p-2" style={{ minHeight: 'auto', padding: '6px' }}>
                    <Archive size={14} />
                  </button>
                  <Link to={`/settings/onboarding/${tmpl.id}/edit`} className="btn btn-primary flex items-center gap-1.5 text-sm px-3 py-1.5" style={{ minHeight: 'auto' }}>
                    <Edit3 size={13} /> Edit
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New template modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={() => setShowNew(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
            width: '100%', maxWidth: 440, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>Create Template</span>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>TEMPLATE NAME *</label>
              <input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Field Technician Onboarding" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>ROLE TYPE</label>
              <input value={newForm.role_type} onChange={e => setNewForm(f => ({ ...f, role_type: e.target.value }))} placeholder="e.g. Field Technician, Supervisor, Contractor" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>DESCRIPTION</label>
              <textarea rows={2} value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description of who this template is for" style={{ width: '100%', resize: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowNew(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={createTemplate} disabled={!newForm.name || saving}>
                {saving ? 'Creating…' : 'Create & Edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAI && (
        <AiGeneratorModal
          onClose={() => setShowAI(false)}
          onSaved={id => { setShowAI(false); load(); window.location.href = `/settings/onboarding/${id}/edit`; }}
        />
      )}
    </div>
  )
}
